import { memo, useCallback, useMemo, useRef } from 'react';

import { Caution, CheckCircled, Pause, Sync } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { Tooltip } from '@air/primitive-tooltip';

import { useWorkspaceActivityStore } from '~/components/v2/store/features/workspace/useWorkspaceActivityStore';
import { WORKSPACE_ACTIVITY_BUTTON } from '~/constants/testIDs';
import { useWorkspaceActivityStatus, type WorkspaceActivityStatus } from '~/hooks/useWorkspaceActivityStatus';

const STATUS_LABEL: Record<WorkspaceActivityStatus, string> = {
  'in-progress': 'Uploading...',
  paused: 'Upload paused',
  error: 'Upload failed',
  completed: 'Uploads complete',
};

export const WorkspaceSidebarFooterActivityButton = memo(() => {
  const { hasTasks, status, count } = useWorkspaceActivityStatus();
  const open = useWorkspaceActivityStore((store) => store.open);
  const toggleOpen = useWorkspaceActivityStore((store) => store.toggleOpen);

  const prefixIcon = useMemo(() => {
    switch (status) {
      case 'in-progress':
        return <Sync className="size-4 animate-spin" />;
      case 'paused':
        return <Pause className="size-4" />;
      case 'error':
        return <Caution className="size-4" />;
      case 'completed':
        return <CheckCircled className="size-4" />;
      default:
        return null;
    }
  }, [status]);

  const observerRef = useRef<ResizeObserver | null>(null);

  const containerRef = useCallback((el: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!el) {
      return;
    }

    const update = () => {
      const rect = el.getBoundingClientRect();
      document.documentElement.style.setProperty(
        '--workspace-activity-button-bottom',
        `${window.innerHeight - rect.top}px`,
      );
    };

    update();
    observerRef.current = new ResizeObserver(update);
    observerRef.current.observe(el);
  }, []);

  if (!hasTasks) {
    return null;
  }

  return (
    <div ref={containerRef} className="inline-flex" data-testid={WORKSPACE_ACTIVITY_BUTTON}>
      <Tooltip label={STATUS_LABEL[status]} side="top">
        <Button
          aria-label={STATUS_LABEL[status]}
          color="grey"
          data-selected={open}
          onClick={toggleOpen}
          prefix={prefixIcon}
          size="small"
        >
          <span className="tabular-nums">{count}</span>
        </Button>
      </Tooltip>
    </div>
  );
});

WorkspaceSidebarFooterActivityButton.displayName = 'WorkspaceSidebarFooterActivityButton';
